import React, { useState, useEffect, useRef } from "react";
import {
  IoSearchOutline,
  IoNotificationsOutline,
  IoMenu,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoArrowUpCircleOutline,
} from "react-icons/io5";
import user from "../../assets/user.png";
import "../layout.css";
import { useSelector } from "react-redux";

const Topbar = ({ toggleSidebar }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);

  const { token, user: authUser } = useSelector((state) => state.auth);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch subscription and build notifications
  useEffect(() => {
    if (!token) return;

    const fetchSubscription = async () => {
      try {
        const res = await fetch("http://localhost:5000/api/payments/my-subscription", {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        const data = await res.json();
        setSubscription(data);

        // Build notifications based on subscription state
        const notifs = [];

        if (data.hasSubscription && data.status === "Active") {
          const planLabel = data.planType === "monthly"
            ? "Base Plan ($39/mo)"
            : "VIP Plan ($79.99/yr)";

          notifs.push({
            id: 1,
            icon: "success",
            title: "Subscription Active",
            message: `You're on the ${planLabel}. Enjoy your benefits!`,
            time: data.purchasedAt
              ? new Date(data.purchasedAt).toLocaleDateString("en-US", { day: "numeric", month: "short" })
              : "Recently",
          });

          // Days left warning
          const daysLeft = Math.max(
            0,
            Math.ceil((new Date(data.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))
          );

          if (daysLeft <= 7) {
            notifs.push({
              id: 2,
              icon: "warning",
              title: "Expiring Soon",
              message: `Your subscription expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}. Renew now!`,
              time: "Now",
            });
          }

          // Upgrade nudge for monthly users
          if (data.planType === "monthly") {
            notifs.push({
              id: 3,
              icon: "upgrade",
              title: "Upgrade to VIP",
              message: "Get 20% off lessons & unlimited replays for just $79.99/yr.",
              time: "",
            });
          }
        }

        if (data.status === "Expired") {
          notifs.push({
            id: 4,
            icon: "warning",
            title: "Subscription Expired",
            message: "Your plan has expired. Renew to keep your benefits.",
            time: "Action needed",
          });
        }

        if (!data.hasSubscription) {
          notifs.push({
            id: 5,
            icon: "upgrade",
            title: "No Active Plan",
            message: "Subscribe to unlock discounts, replays, and more!",
            time: "",
          });
        }

        setNotifications(notifs);
      } catch (err) {
        console.error("Topbar subscription fetch failed:", err);
      }
    };

    fetchSubscription();
  }, [token]);

  const iconForType = (type) => {
    if (type === "success") return <IoCheckmarkCircleOutline size={18} color="#22c55e" />;
    if (type === "warning") return <IoTimeOutline size={18} color="#f59e0b" />;
    if (type === "upgrade") return <IoArrowUpCircleOutline size={18} color="#e6be5d" />;
    return <IoNotificationsOutline size={18} color="#888" />;
  };

  // Get display name — first name from registered name
  const displayName = authUser?.name
    ? authUser.name.split(" ")[0]
    : authUser?.firstName || "Student";

  return (
    <div className="topbar">
      {/* Left: Brand + Menu */}
      <div className="heading-topbar">
        <button className="menu-btn" onClick={toggleSidebar}>
          <IoMenu size={24} />
        </button>
        <h1 className="brand-title">Seth White Productions</h1>
      </div>

      {/* Middle: Search */}
      <div className="search">
        <IoSearchOutline className="search-icon" />
        <input type="text" placeholder="Search..." />
      </div>

      {/* Right: Notifications + User */}
      <div className="detail">

        {/* Notifications */}
        <div className="notification" ref={notifRef} style={{ position: "relative" }}>
          <button
            onClick={() => setShowNotifications((prev) => !prev)}
            style={{ background: "none", border: "none", cursor: "pointer", position: "relative", padding: 0 }}
          >
            <IoNotificationsOutline size={24} />
            {notifications.length > 0 && (
              <span className="badge">{notifications.length}</span>
            )}
          </button>

          {/* Dropdown */}
          {showNotifications && (
            <div style={{
              position: "absolute",
              top: "calc(100% + 12px)",
              right: 0,
              width: "300px",
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              zIndex: 1000,
              overflow: "hidden",
            }}>
              <div style={{
                padding: "12px 16px",
                borderBottom: "1px solid #333",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>
                  Notifications
                </span>
                <span style={{
                  background: "#e6be5d",
                  color: "#000",
                  fontSize: "11px",
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: "20px",
                }}>
                  {notifications.length} new
                </span>
              </div>

              {notifications.length === 0 ? (
                <div style={{ padding: "1.5rem", textAlign: "center", color: "#888", fontSize: "13px" }}>
                  No notifications
                </div>
              ) : (
                notifications.map((notif) => (
                  <div key={notif.id} style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #222",
                    display: "flex",
                    gap: "10px",
                    alignItems: "flex-start",
                    cursor: "default",
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#222"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ marginTop: "2px", flexShrink: 0 }}>
                      {iconForType(notif.icon)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: "#fff", fontSize: "13px", fontWeight: 600, margin: "0 0 2px" }}>
                        {notif.title}
                      </p>
                      <p style={{ color: "#9ca3af", fontSize: "12px", margin: "0 0 4px", lineHeight: 1.4 }}>
                        {notif.message}
                      </p>
                      {notif.time && (
                        <p style={{ color: "#555", fontSize: "11px", margin: 0 }}>
                          {notif.time}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* User */}
        <div className="user-detail">
          <span className="username">{displayName}</span>
          <img src={user} alt="user" />
        </div>
      </div>
    </div>
  );
};

export default Topbar;